import type React from 'react';
import { hexToHSL, normalizeThemeColorValue } from '@ai-content-hub/shared-styles';
import type { ThemeConfig } from '../schema';

/**
 * WCAG relative luminance from a `#rrggbb` hex color. Returns a dark
 * foreground HSL for light colors (luminance > 0.55) and a light foreground
 * for dark colors — replaces the hardcoded white/black foregrounds the
 * legacy `ContentWrapper.tsx` mapping used, which produced unreadable text
 * on light custom primaries (e.g. a yellow brand color with white text).
 */
function foregroundFor(hex: string | undefined): string | undefined {
    if (!hex) return undefined;
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!match) return undefined;

    const [r, g, b] = [match[1], match[2], match[3]].map((channel) => {
        const c = parseInt(channel, 16) / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

    return luminance > 0.55 ? '0 0% 10%' : '0 0% 100%';
}

// EAA-1671 (Fable 5 review): normalize rather than just validate — a valid
// 3-digit (`#abc`) or 8-digit-with-alpha hex passes `isValidThemeColorValue`
// but isn't the exact-6-digit shape `hexToHSL`/`foregroundFor` parse, so it
// was silently falling through to their `0 0% 0%` (black) failure sentinel.
// Normalizing guarantees callers of `themeToCssVars` — including preset/
// override layers that never pass through the resolver's sanitizer — always
// get canonical 6-digit input.
function validHex(hex: string | undefined): string | undefined {
    return normalizeThemeColorValue(hex) ?? undefined;
}

/** Strip keys whose value is `undefined` — React warns on undefined custom props in style objects. */
function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
    const result: Partial<T> = { ...obj };
    for (const key of Object.keys(result) as (keyof T)[]) {
        if (result[key] === undefined) delete result[key];
    }
    return result;
}

/**
 * Sparse CSS custom-property emission for a resolved content theme.
 *
 * Emits vars ONLY for fields `theme` actually sets — an item that sets just
 * `primaryColor` shadows `--primary` in its subtree while every other var
 * (`--success`, `--background`, …) falls through to the tenant values
 * `shared-styles`' `applyTheme` already wrote on `:root`. Never emit a
 * default/fallback value for an unset field — that would mask the tenant
 * color underneath.
 */
export function themeToCssVars(theme: ThemeConfig | undefined): React.CSSProperties {
    if (!theme) return {};

    const primaryColor = validHex(theme.primaryColor);
    const errorColor = validHex(theme.errorColor);
    const successColor = validHex(theme.successColor);
    const warningColor = validHex(theme.warningColor);
    const backgroundColor = validHex(theme.backgroundColor);
    const textColor = validHex(theme.textColor);

    return stripUndefined({
        // shadcn HSL vars (scoped — cascade to kit components in the subtree)
        '--primary': primaryColor ? hexToHSL(primaryColor) : undefined,
        '--primary-foreground': foregroundFor(primaryColor),
        '--destructive': errorColor ? hexToHSL(errorColor) : undefined,
        '--destructive-foreground': foregroundFor(errorColor),
        '--success': successColor ? hexToHSL(successColor) : undefined,
        '--success-foreground': foregroundFor(successColor),
        '--warning': warningColor ? hexToHSL(warningColor) : undefined,
        '--warning-foreground': foregroundFor(warningColor),
        // Legacy content-* vars (hex) — tiptap.css depends on these names
        '--content-primary': primaryColor,
        '--content-success': successColor,
        '--content-error': errorColor,
        '--content-warning': warningColor,
        '--content-bg': backgroundColor,
        '--content-text': textColor,
        '--content-radius': theme.borderRadius,
        '--content-font': theme.fontFamily,
        '--content-font-size': theme.fontSize,
    }) as React.CSSProperties;
}

/** Visual styles applied to the wrapper element itself (bg, text, font, radius). */
export function themeToWrapperStyles(theme: ThemeConfig | undefined): React.CSSProperties {
    if (!theme) return {};

    return stripUndefined({
        backgroundColor: validHex(theme.backgroundColor),
        color: validHex(theme.textColor),
        fontFamily: theme.fontFamily,
        fontSize: theme.fontSize,
        lineHeight: theme.fontSize ? '1.5' : undefined,
        borderRadius: theme.borderRadius,
    }) as React.CSSProperties;
}
